/**
 * 音频管理器初始化 Mixin
 * 仅用于 App.vue，负责连接 audioManager 和 Vuex Store
 * 注意：此 mixin 只应在 App.vue 中使用一次
 */

import { mapActions, mapMutations, mapGetters } from "vuex";
import audioManager from "@/utils/audioManager";
import {
  getAudiobookChapterDetail,
  getLessionsChapterDetail
} from "@/api/publicApi";

export default {
  data() {
    return {
      // 用于防止循环更新的标志
      isSeekingFromUser: false,
      isPlayingFromManager: false,
      // 上一个播放的 track ID，用于检测是否切换了音频
      lastTrackId: null,
      // 播放错误重试相关
      playErrorRetryCount: 0,
      maxPlayErrorRetries: 2, // 最大重试次数
      // 加载错误重试相关
      loadErrorRetryCount: 0,
      maxLoadErrorRetries: 2, // 最大重试次数
      isRefreshingUrl: false, // 是否正在刷新URL
      refreshingTrackId: null, // 当前正在刷新的track ID
      shouldSyncOnPlay: false, // 是否在下次播放时同步进度（用于切换音频后）
      seekSyncTimer: null // seek 同步的防抖定时器
    };
  },

  computed: {
    ...mapGetters("audio", ["canPlayNext"])
  },

  watch: {
    /**
     * 监听当前音轨变化，加载新音频
     */
    "$store.state.audio.currentTrack": {
      async handler(newTrack, oldTrack) {
        if (!newTrack) {
          console.log("[AudioManagerInit] 当前音轨为空，停止播放");
          audioManager.destroy();
          this.lastTrackId = null;
          return;
        }

        // 检查是否真的切换了音频（通过 customId 或 id 判断）
        const newTrackId = newTrack.customId;
        const oldTrackId = oldTrack ? oldTrack.customId : null;

        if (newTrackId === oldTrackId && this.lastTrackId === newTrackId) {
          console.log("[AudioManagerInit] 相同音轨，无需重新加载");
          return;
        }

        // 如果切换了音频，先同步上一首的播放进度
        if (oldTrack && oldTrackId !== newTrackId) {
          console.log("[AudioManagerInit] 检测到切换音频，同步上一首播放进度");

          // 在切换前保存旧音频的播放时间
          // 优先从 store 获取（这是最准确的，因为它是实时更新的）
          // 如果 store 中的时间为0，尝试从 audioManager 获取（此时 audioManager 还是旧音频）
          let previousTime = this.$store.state.audio.currentTime;
          if (!previousTime || previousTime <= 0) {
            previousTime = audioManager.getCurrentTime
              ? audioManager.getCurrentTime()
              : 0;
          }
          console.log("[AudioManagerInit] 保存的上一首播放时间:", previousTime);
          await this.syncPreviousTrackProgress(oldTrack, previousTime);
        }

        console.log(
          "[AudioManagerInit] 切换音轨:",
          newTrack.title || newTrack.customId
        );
        this.lastTrackId = newTrackId;
        // 切换音轨时重置重试计数
        this.playErrorRetryCount = 0;
        this.loadErrorRetryCount = 0;
        // 标记需要在播放时同步一次进度
        this.shouldSyncOnPlay = true;

        this.SET_CURRENT_TIME(0);

        // 如果正在刷新URL，且是不同track，清除之前的刷新操作
        if (this.isRefreshingUrl && this.refreshingTrackId !== newTrackId) {
          console.log(
            "[AudioManagerInit] 检测到切换音轨，取消之前的URL刷新操作"
          );
          this.isRefreshingUrl = false;
          this.refreshingTrackId = null;
        }

        try {
          // 获取最新的音频链接
          const audioUrl = await this.refreshAudioUrl(newTrack);

          if (!audioUrl) {
            throw new Error("无法获取音频链接");
          }

          // 加载新音频（audioManager 会自动触发 loadingChange 事件）
          await audioManager.load({
            id: newTrack.id,
            customId: newTrack.customId,
            url: audioUrl,
            title: newTrack.title,
            ...newTrack
          });

          // 如果有进度信息，跳转到上次播放位置
          if (newTrack.progress && newTrack.progress > 0) {
            console.log(
              `[AudioManagerInit] 恢复播放进度: ${newTrack.progress}秒`
            );
            audioManager.seek(newTrack.progress);
          }

          console.log("[AudioManagerInit] 加载完成，自动播放");
          audioManager.play();
        } catch (error) {
          console.error("[AudioManagerInit] 加载音频失败:", error);
          // 可以在这里显示错误提示
          this.$message?.error("音频加载失败，请重试");
        }
      },
      immediate: false
    },

    /**
     * 监听播放状态变化
     */
    "$store.state.audio.isPlaying": {
      handler(newVal) {
        if (this.isPlayingFromManager) {
          // 如果是从 audioManager 触发的状态变化，忽略，防止循环
          return;
        }

        console.log("[AudioManagerInit] 播放状态变化:", newVal);

        if (!audioManager.currentAudio) {
          console.warn("[AudioManagerInit] AudioManager 未初始化");
          return;
        }

        if (newVal) {
          audioManager.play();
        } else {
          audioManager.pause();
        }
      },
      immediate: false
    }
  },

  mounted() {
    console.log("[AudioManagerInit] 初始化音频播放器事件监听");
    this.initAudioManager();
  },

  beforeDestroy() {
    console.log("[AudioManagerInit] 清理音频播放器");
    this.cleanupAudioManager();
  },

  methods: {
    ...mapActions("audio", [
      "playNext",
      "syncPlayProgress",
      "syncPlayCount",
      "startProgressSync",
      "stopProgressSync"
    ]),

    ...mapActions(["openPayDialog"]),

    ...mapMutations("audio", [
      "SET_PLAYING_STATE",
      "SET_LOADING_STATE",
      "SET_CURRENT_TIME",
      "SET_DURATION",
      "UPDATE_LOCAL_PROGRESS"
    ]),

    /**
     * 初始化 AudioManager 事件监听
     */
    initAudioManager() {
      // 加载状态变化
      audioManager.on("loadingChange", ({ isLoading }) => {
        console.log("[AudioManagerInit] Loading 状态变化:", isLoading);
        this.SET_LOADING_STATE(isLoading);
      });

      // 加载完成
      audioManager.on("load", ({ duration }) => {
        console.log("[AudioManagerInit] 音频加载完成，时长:", duration);
        this.SET_DURATION(duration);
      });

      // 加载错误
      audioManager.on("loadError", async error => {
        console.error("[AudioManagerInit] 音频加载错误:", error);
        // 对于错误码2（可能是403等网络错误），尝试刷新URL并重新加载
        // 错误码2通常表示网络错误或无法加载资源（包括403）
        if (error === 2 || error === 4) {
          console.log(
            "[AudioManagerInit] 检测到加载错误（错误码2，可能是403），尝试刷新URL并重新加载"
          );
          await this.handleLoadError(error);
        } else {
          // 其他加载错误，只显示错误提示
          this.$message?.error("音频加载失败，请重试");
        }
      });

      // 开始播放
      audioManager.on("play", () => {
        console.log("[AudioManagerInit] AudioManager 开始播放");
        this.isPlayingFromManager = true;
        this.SET_PLAYING_STATE(true);
        // 启动播放进度定时同步
        this.startProgressSync();
        // 同步播放次数（只在开始播放时调用一次）
        this.syncPlayCount();

        // 如果是切换音频后的首次播放，同步一次当前进度
        if (this.shouldSyncOnPlay) {
          console.log("[AudioManagerInit] 切换音频后首次播放，同步当前进度");
          // 延迟一小段时间，确保音频已经开始播放并有进度
          setTimeout(() => {
            this.syncPlayProgress();
            this.shouldSyncOnPlay = false;
          }, 500);
        }

        this.$nextTick(() => {
          this.isPlayingFromManager = false;
        });
      });

      // 暂停播放
      audioManager.on("pause", () => {
        console.log("[AudioManagerInit] AudioManager 暂停播放");
        this.isPlayingFromManager = true;
        this.SET_PLAYING_STATE(false);
        // 暂停时同步一次播放进度
        this.syncPlayProgress();
        this.$nextTick(() => {
          this.isPlayingFromManager = false;
        });
      });

      // 停止播放
      audioManager.on("stop", () => {
        console.log("[AudioManagerInit] AudioManager 停止播放");
        this.isPlayingFromManager = true;
        this.SET_PLAYING_STATE(false);
        this.SET_CURRENT_TIME(0);
        // 停止时同步一次播放进度
        this.syncPlayProgress();
        this.$nextTick(() => {
          this.isPlayingFromManager = false;
        });
      });

      // 播放结束
      audioManager.on("end", () => {
        console.log("[AudioManagerInit] AudioManager 播放结束");
        this.isPlayingFromManager = true;

        // 同步播放进度（标记为已完成）
        this.syncPlayProgress();

        let playlist = this.$store.state.audio.playlist;
        let currentIndex = this.$store.state.audio.currentIndex;
        if (playlist[currentIndex + 1]) {
          let nextChapterInfo = playlist[currentIndex + 1];
          if (!nextChapterInfo.isUnLock) {
            // console.log("come in the next chapter");
            let customId = nextChapterInfo.customId;
            let parts = customId.split("-");
            let payType = parseInt(parts[0]);
            let lessionOrAudioBookId = parseInt(parts[1]);

            this.openPayDialog({
              payType: parseInt(payType),
              lessionOrAudioBookId: parseInt(lessionOrAudioBookId)
            });
            this.$nextTick(() => {
              this.isPlayingFromManager = false;
            });
            return;
          }
        }

        // 判断是否可以播放下一首
        if (this.canPlayNext) {
          console.log("[AudioManagerInit] 自动播放下一首");
          // 播放下一首
          this.playNext();
        } else {
          console.log("[AudioManagerInit] 已是最后一首，暂停播放");
          // 最后一首播放完，暂停播放
          this.SET_PLAYING_STATE(false);
          // 重置进度到开头
          this.SET_CURRENT_TIME(0);
          audioManager.seek(0);
        }

        this.$nextTick(() => {
          this.isPlayingFromManager = false;
        });
      });

      // 播放进度更新
      audioManager.on("progress", ({ currentTime, duration }) => {
        if (!this.isSeekingFromUser) {
          this.SET_CURRENT_TIME(currentTime);
          this.SET_DURATION(duration);
          // 更新本地进度（currentTrack 和 playlist）
          this.UPDATE_LOCAL_PROGRESS(currentTime);
        }
      });

      // 跳转完成
      audioManager.on("seek", ({ currentTime }) => {
        console.log("[AudioManagerInit] 跳转完成:", currentTime);
        // 设置标志，防止 progress 事件立即覆盖 seek 的结果
        this.isSeekingFromUser = true;
        this.SET_CURRENT_TIME(currentTime);

        // 清除之前的同步定时器
        if (this.seekSyncTimer) {
          clearTimeout(this.seekSyncTimer);
        }

        // 延迟同步进度（防抖，避免频繁同步）
        this.seekSyncTimer = setTimeout(() => {
          console.log("[AudioManagerInit] 用户切换进度，同步播放进度");
          this.syncPlayProgress();
          this.seekSyncTimer = null;
        }, 1000); // 1秒后同步，避免频繁操作时重复同步

        // 在下一个 tick 重置标志，允许后续的 progress 事件正常更新
        this.$nextTick(() => {
          this.isSeekingFromUser = false;
        });
      });

      // 播放错误
      audioManager.on("playError", async error => {
        console.error("[AudioManagerInit] 播放错误:", error);
        this.isPlayingFromManager = true;
        this.SET_PLAYING_STATE(false);
        this.$nextTick(() => {
          this.isPlayingFromManager = false;
        });

        // 处理播放错误，尝试刷新URL并重新加载
        await this.handlePlayError(error);
      });

      // URL过期回调（当检测到403时立即触发，避免继续重试）
      audioManager.on("urlExpired", async () => {
        console.warn("[AudioManagerInit] 检测到URL过期，立即刷新URL");
        const currentTrack = this.$store.state.audio.currentTrack;
        if (currentTrack) {
          // 立即刷新URL并重新加载，避免继续重试
          await this.refreshAndReloadAudio(currentTrack);
        }
      });

      console.log("[AudioManagerInit] AudioManager 事件监听已初始化");
    },

    /**
     * 清理 AudioManager
     */
    cleanupAudioManager() {
      // 移除所有事件监听
      audioManager.off("loadingChange");
      audioManager.off("load");
      audioManager.off("loadError");
      audioManager.off("play");
      audioManager.off("pause");
      audioManager.off("stop");
      audioManager.off("end");
      audioManager.off("progress");
      audioManager.off("seek");
      audioManager.off("playError");
      audioManager.off("urlExpired");

      // 清除 seek 同步定时器
      if (this.seekSyncTimer) {
        clearTimeout(this.seekSyncTimer);
        this.seekSyncTimer = null;
      }

      // 停止播放进度同步
      this.stopProgressSync();

      // 销毁 AudioManager（可选，根据需求决定是否保留播放状态）
      // audioManager.destroy();
    },

    /**
     * 处理加载错误
     * @param {number} error - 错误代码
     */
    async handleLoadError(error) {
      const currentTrack = this.$store.state.audio.currentTrack;
      if (!currentTrack) {
        console.warn("[AudioManagerInit] 没有当前音轨，无法处理加载错误");
        this.$message?.error("音频加载失败，请重试");
        return;
      }

      const currentTrackId = currentTrack.customId || currentTrack.id;

      // 如果正在刷新URL，检查是否是同一个track
      if (this.isRefreshingUrl) {
        if (this.refreshingTrackId === currentTrackId) {
          console.log(
            "[AudioManagerInit] 正在刷新当前track的URL，跳过重复处理"
          );
          return;
        } else {
          console.log(
            "[AudioManagerInit] 检测到track已切换，取消之前的刷新操作"
          );
          // 清除之前的刷新操作
          this.isRefreshingUrl = false;
          this.refreshingTrackId = null;
        }
      }

      // 检查是否超过最大重试次数
      if (this.loadErrorRetryCount >= this.maxLoadErrorRetries) {
        console.error("[AudioManagerInit] 加载错误重试次数已达上限，停止重试");
        this.loadErrorRetryCount = 0; // 重置计数
        this.$message?.error("音频加载失败，请重试");
        return;
      }

      // 加载错误时，可能是URL过期（403），直接尝试刷新URL并重新加载
      // 即使URL没有过期，刷新后重新加载也不会有什么问题
      console.log(
        `[AudioManagerInit] 加载错误（错误码: ${error}），尝试刷新URL并重新加载 (${this.loadErrorRetryCount + 1}/${this.maxLoadErrorRetries})`
      );
      this.loadErrorRetryCount++;
      await this.refreshAndReloadAudio(currentTrack);
    },

    /**
     * 处理播放错误
     * @param {number} error - 错误代码
     */
    async handlePlayError(error) {
      const currentTrack = this.$store.state.audio.currentTrack;
      if (!currentTrack) {
        console.warn("[AudioManagerInit] 没有当前音轨，无法处理播放错误");
        this.$message?.error("播放失败，请重试");
        return;
      }

      const currentTrackId = currentTrack.customId || currentTrack.id;

      // 如果正在刷新URL，检查是否是同一个track
      if (this.isRefreshingUrl) {
        if (this.refreshingTrackId === currentTrackId) {
          console.log(
            "[AudioManagerInit] 正在刷新当前track的URL，跳过重复处理"
          );
          return;
        } else {
          console.log(
            "[AudioManagerInit] 检测到track已切换，取消之前的刷新操作"
          );
          // 清除之前的刷新操作
          this.isRefreshingUrl = false;
          this.refreshingTrackId = null;
        }
      }

      // 检查是否超过最大重试次数
      if (this.playErrorRetryCount >= this.maxPlayErrorRetries) {
        console.error("[AudioManagerInit] 播放错误重试次数已达上限，停止重试");
        this.playErrorRetryCount = 0; // 重置计数
        this.$message?.error("播放失败，请重试");
        return;
      }

      // 播放错误时，可能是URL过期（403），直接尝试刷新URL并重新加载
      // 即使URL没有过期，刷新后重新加载也不会有什么问题
      console.log(
        `[AudioManagerInit] 播放错误（错误码: ${error}），尝试刷新URL并重新加载 (${this.playErrorRetryCount + 1}/${this.maxPlayErrorRetries})`
      );
      this.playErrorRetryCount++;
      await this.refreshAndReloadAudio(currentTrack);
    },

    /**
     * 刷新URL并重新加载音频
     * @param {Object} track - 音轨对象
     */
    async refreshAndReloadAudio(track) {
      const trackId = track.customId || track.id;

      // 如果正在刷新URL，检查是否是同一个track
      if (this.isRefreshingUrl) {
        if (this.refreshingTrackId === trackId) {
          console.log("[AudioManagerInit] 正在刷新URL，跳过重复请求");
          return;
        } else {
          console.log(
            "[AudioManagerInit] 检测到track已切换，取消之前的刷新操作"
          );
          // 清除之前的刷新操作
          this.isRefreshingUrl = false;
          this.refreshingTrackId = null;
        }
      }

      this.isRefreshingUrl = true;
      this.refreshingTrackId = trackId;

      try {
        // 在刷新过程中，定期检查track是否已切换
        const checkTrackChanged = () => {
          const currentTrack = this.$store.state.audio.currentTrack;
          const currentTrackId = currentTrack
            ? currentTrack.customId || currentTrack.id
            : null;
          return currentTrackId !== trackId;
        };

        // 保存当前播放位置
        const currentTime = audioManager.getCurrentTime();
        console.log(`[AudioManagerInit] 保存当前播放位置: ${currentTime}秒`);

        // 先停止当前播放，避免继续重试旧的URL
        if (audioManager.currentAudio) {
          console.log("[AudioManagerInit] 停止当前播放，准备刷新URL");
          audioManager.stop();
          // 等待一小段时间，确保停止重试
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        // 检查track是否已切换
        if (checkTrackChanged()) {
          console.log("[AudioManagerInit] track已切换，取消刷新操作");
          this.isRefreshingUrl = false;
          this.refreshingTrackId = null;
          return;
        }

        // 获取最新的音频链接
        const audioUrl = await this.refreshAudioUrl(track);

        // 再次检查track是否已切换
        if (checkTrackChanged()) {
          console.log("[AudioManagerInit] track已切换，取消刷新操作");
          this.isRefreshingUrl = false;
          this.refreshingTrackId = null;
          return;
        }

        if (!audioUrl) {
          throw new Error("无法获取音频链接");
        }

        // 重新加载音频（load方法内部会先destroy旧的实例）
        await audioManager.load({
          id: track.id,
          customId: track.customId,
          url: audioUrl,
          title: track.title,
          ...track
        });

        // 最后一次检查track是否已切换
        if (checkTrackChanged()) {
          console.log("[AudioManagerInit] track已切换，取消恢复播放");
          this.isRefreshingUrl = false;
          this.refreshingTrackId = null;
          return;
        }

        // 恢复播放位置
        if (currentTime > 0) {
          console.log(`[AudioManagerInit] 恢复播放位置: ${currentTime}秒`);
          audioManager.seek(currentTime);
        }

        // 重新开始播放
        console.log("[AudioManagerInit] 重新加载完成，恢复播放");
        audioManager.play();

        // 重置重试计数（成功刷新并重新加载后）
        this.playErrorRetryCount = 0;
        this.loadErrorRetryCount = 0;
      } catch (error) {
        console.error("[AudioManagerInit] 刷新并重新加载音频失败:", error);
        // 如果刷新失败，不重置重试计数，允许继续重试
        this.$message?.error("播放失败，请重试");
      } finally {
        // 只有在当前track没有切换的情况下才清除标志
        if (this.refreshingTrackId === trackId) {
          this.isRefreshingUrl = false;
          this.refreshingTrackId = null;
        }
      }
    },

    /**
     * 刷新音频URL（从服务器获取最新的音频链接）
     * @param {Object} track - 音轨对象
     * @returns {Promise<string>} 返回最新的音频URL
     */
    async refreshAudioUrl(track) {
      try {
        if (!track || !track.id) {
          console.error(
            "[AudioManagerInit] refreshAudioUrl: 缺少 track 或 track.id"
          );
          return null;
        }

        const playlistInfo = this.$store.state.audio.playlistInfo;

        if (!playlistInfo || !playlistInfo.type) {
          console.warn(
            "[AudioManagerInit] 没有 playlistInfo 信息，使用 track 自带的 url"
          );
          return track.audioPath;
        }

        console.log(
          `[AudioManagerInit] 刷新音频链接: type=${playlistInfo.type}, chapterId=${track.id}`
        );

        let response = null;

        // 根据 type 调用不同的接口
        if (playlistInfo.type === 1) {
          // 有声书
          response = await getAudiobookChapterDetail({
            id: track.id
          });
        } else if (playlistInfo.type === 2) {
          // 课程
          response = await getLessionsChapterDetail({
            id: track.id
          });
        } else {
          console.error(
            "[AudioManagerInit] 未知的资源类型:",
            playlistInfo.type
          );
          return track.url || track.audioPath;
        }

        // 检查响应
        if (response?.code === 0) {
          const audioPath = response.data.chapter.audioPath;

          if (audioPath) {
            console.log("[AudioManagerInit] 音频链接刷新成功:", audioPath);
            return audioPath;
          } else {
            console.warn("[AudioManagerInit] 响应中没有 audioPath");
            return track.url || track.audioPath;
          }
        } else {
          console.error("[AudioManagerInit] 获取音频链接失败:", response);
          return track.url || track.audioPath;
        }
      } catch (error) {
        console.error("[AudioManagerInit] refreshAudioUrl 错误:", error);
        // 出错时返回原有的 url
        return track.url || track.audioPath;
      }
    },

    /**
     * 同步上一首音频的播放进度（在切换音频时调用）
     * @param {Object} previousTrack - 上一首音轨对象
     * @param {number} previousTime - 上一首的播放时间（秒），如果未提供则从 store 获取
     */
    async syncPreviousTrackProgress(previousTrack, previousTime) {
      console.log(
        "[AudioManagerInit] 同步上一首播放进度:",
        previousTrack.title || previousTrack.customId
      );

      // 使用传入的播放时间，如果没有传入则从 store 获取
      // 注意：此时不应该从 audioManager 获取，因为可能已经加载了新音频
      const currentTime =
        previousTime !== undefined
          ? previousTime
          : this.$store.state.audio.currentTime || 0;

      // 如果播放时间为0或无效，跳过同步
      if (!currentTime || currentTime <= 0) {
        console.log("[AudioManagerInit] 播放时间为0，跳过上一首播放进度同步");
        return;
      }

      // 获取 playlistInfo，确保使用正确的 type
      const playlistInfo = this.$store.state.audio.playlistInfo;

      // 使用 store 中的 syncPlayProgress，传入上一首的 track、播放时间和 playlistInfo
      await this.syncPlayProgress({
        track: previousTrack,
        time: currentTime,
        playlistInfo: playlistInfo
      });
    }
  }
};
